import clsx from "clsx";

import {
  FilterTag,
  FilterTagChild,
} from "features/filters/components/FilterTag";
import { LABEL_COLORS } from "features/labels/constants";
import useAllLabels from "features/labels/datahooks/useAllLabels";
import type { LeadsSearchParams } from "features/leads/types";

import ProfileImage from "common/components/ui/ProfileImage";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useWorkspaceAccounts from "common/datahooks/useWorkspaceAccounts";
import type { SelectedFacetParam } from "common/types";

import type { ReactNode } from "react";

interface SelectionTagsProps {
  searchKey: keyof Omit<LeadsSearchParams<never>, "keywords" | "favorite">;
  selection: SelectedFacetParam[];
  onRemoveFilter: (facet: SelectedFacetParam) => void;
}

export default function SelectionTags({
  searchKey,
  selection,
  onRemoveFilter,
}: SelectionTagsProps) {
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { allLabels } = useAllLabels("workspace");
  const { workspaceAccounts } = useWorkspaceAccounts(workspaceId);

  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5 px-2">
      {selection.map((facet) => {
        const { id } = facet;

        // labels and accounts are only stored by id, so they are resolved here
        let leftComponent: ReactNode;
        let label = facet.name;

        if (searchKey === "labels") {
          const selectedLabel = allLabels?.find(
            (workspaceLabel) => workspaceLabel.id === id,
          );

          //no label filter has a special id
          if (id === "NO_LABEL") {
            label = "No label";
          } else if (!allLabels) {
            label = "Loading...";
          } else if (!selectedLabel) {
            label = "Missing label";
          } else {
            label = selectedLabel.name;
            leftComponent = (
              <div
                className={clsx(
                  "size-3.5 shrink-0 rounded",
                  LABEL_COLORS[selectedLabel.color].bgClassName,
                )}
              />
            );
          }
        } else if (searchKey === "lead_of") {
          const account = workspaceAccounts?.find(
            ({ id: accountId }) => accountId === id,
          );

          if (!workspaceAccounts) {
            label = "Loading accounts...";
          } else if (!account) {
            label = "Missing account";
          } else {
            label = account.full_name;
            leftComponent = (
              <ProfileImage size="3xs" src={account.picture_url} />
            );
          }
        }

        return (
          <FilterTag key={id} onRemove={() => onRemoveFilter(facet)}>
            <FilterTagChild>
              {leftComponent}
              {label}
            </FilterTagChild>
          </FilterTag>
        );
      })}
    </div>
  );
}
